import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { notification } from '@surecompanies/core_components'
import isNil from 'lodash/isNil'
import { extractError } from '../../api/extract-error'
import { getAllUsers } from '../../api/user-invitation.api'
import {
  GetAllUsersRequestDto,
  UserListRow,
} from '../../api/dto/user-invitation.dto'
import { buildAccessRemovalPlan } from './accessRemovalBuckets'
import { buildGrantBuckets } from './grantBuckets'

interface UseUsersSelectionParams {
  /** Rows on the page in hand. */
  records: UserListRow[]
  /** What the table is showing, which is what a select-all must cover. */
  filters: GetAllUsersRequestDto
}

/**
 * Which users are ticked, and what acting on them would do. Selection survives
 * paging, so it holds the rows it has seen rather than only the current page.
 */
export const useUsersSelection = ({
  records,
  filters,
}: UseUsersSelectionParams) => {
  const [selectedEmails, setSelectedEmails] = useState<string[]>([])
  const [isSelectingAll, setIsSelectingAll] = useState(false)

  const rowByEmail = useMemo(
    () => new Map(records.map((row) => [row.email, row])),
    [records]
  )

  // Bulk selection persists across server-paginated pages (preserveSelectedRowKeys),
  // but each page only returns its own rows. Cache every row we've seen so a
  // selection made on page 1 still resolves to its data once the table is on
  // page 2. Refreshed each render so cached rows track the latest server data.
  const seenRowsRef = useRef<Map<string, UserListRow>>(new Map())
  useEffect(() => {
    for (const row of records) {
      seenRowsRef.current.set(row.email, row)
    }
  }, [records])

  // Rows on the current page always resolve from rowByEmail; the cache only
  // covers earlier pages, which it was filled with while they were on screen.
  const selectedRows = useMemo(
    () =>
      selectedEmails
        .map((email) => rowByEmail.get(email) ?? seenRowsRef.current.get(email))
        .filter((row): row is UserListRow => !isNil(row)),
    [selectedEmails, rowByEmail]
  )

  const selectionBuckets = useMemo(
    () => buildGrantBuckets(selectedRows),
    [selectedRows]
  )

  const selectionRemovalPlan = useMemo(
    () => buildAccessRemovalPlan(selectedRows),
    [selectedRows]
  )

  const handleSelectionChange = useCallback(
    (emails: string[]) => setSelectedEmails(emails),
    []
  )

  const handleClearSelection = useCallback(() => setSelectedEmails([]), [])

  /**
   * Selects every user the given filters match, not just the page in hand, so a
   * bulk action over a whole status is one click. The rows come back with the
   * emails and go into the cache, which is what the bulk bar and the confirm
   * step classify against.
   */
  const selectAllMatching = useCallback(
    async (matching: GetAllUsersRequestDto) => {
      setIsSelectingAll(true)
      try {
        const { data: rows, total: matched } = await getAllUsers(matching)
        for (const row of rows) {
          seenRowsRef.current.set(row.email, row)
        }
        setSelectedEmails(rows.map((row) => row.email))
        if (matched > rows.length) {
          notification.warning({
            message: `Selected the first ${rows.length} of ${matched} users`,
            description: 'Narrow the filters to act on the rest.',
          })
        }
      } catch (error) {
        notification.error({
          message: extractError(error, 'Failed to select all users'),
        })
      } finally {
        setIsSelectingAll(false)
      }
    },
    []
  )

  const handleSelectAllFiltered = useCallback(
    () => selectAllMatching(filters),
    [filters, selectAllMatching]
  )

  return {
    selectedEmails,
    selectedRows,
    selectionBuckets,
    selectionRemovalPlan,
    isSelectingAll,
    handleSelectionChange,
    handleClearSelection,
    handleSelectAllFiltered,
    selectAllMatching,
  }
}
